import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import {
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Calendar,
  ShoppingCart,
  IndianRupee,
  Clock,
  CheckCircle2,
  Eye,
  Pencil,
  Trash2,
  X,
  LayoutDashboard,
  FileUp,
  Loader2
} from 'lucide-react';
import styles from './PurchaseOrders.module.css';
import Pagination from '../../components/Common/Pagination/Pagination';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import Footer from '../../components/Common/Footer/Footer';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import { SlidersHorizontal as Filters } from 'lucide-react';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const pathPrefix = '/purchase-orders';
  const pageTitle = 'Purchase Orders';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const mainContentRef = useRef(null);
  const isInitialMount = useRef(true);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    vendor_id: '',
    project_id: '',
    status: ''
  });

  const [tableLoading, setTableLoading] = useState(true);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1
  });
  const [filterData, setFilterData] = useState({
    projects: [],
    vendors: []
  });
  const [summaryData, setSummaryData] = useState({
    total_purchase_orders: 0,
    total_po_value: 0,
    pending_payments: 0,
    completed_pos: 0
  });

  const fetchFilterData = async () => {
    try {
      const [projectsRes, vendorsRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/vendors-dropdown')
      ]);
      setFilterData({
        projects: projectsRes.data.data || [],
        vendors: vendorsRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchPurchaseOrders = async () => {
    setTableLoading(true);
    try {
      const response = await api.get('/purchase-orders', {
        skipLoader: true,
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          ...filters
        }
      });
      if (response.data.success) {
        setPurchaseOrders(response.data.data.data);
        setPaginationData({
          total: response.data.data.total,
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page
        });
      }
    } catch (error) {
      console.error('Error fetching Purchase Orders:', error);
      if (error.response?.status === 404) {
        setPurchaseOrders([]);
        setPaginationData(prev => ({ ...prev, total: 0, last_page: 1 }));
      } else {
        toast.error('Failed to load Purchase Orders');
      }
    } finally {
      setTableLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/purchase-orders-summary');
      if (response.data.success) {
        setSummaryData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
    fetchSummary();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const scrollTimeout = setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
    return () => clearTimeout(scrollTimeout);
  }, [currentPage]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      vendor_id: '',
      project_id: '',
      status: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };

    const handleScroll = () => {
      if (openMenuIndex !== null) setOpenMenuIndex(null);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openMenuIndex]);

  const toggleActionMenu = (index, e) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 160
      });
      setOpenMenuIndex(index);
    }
  };

  const handleDeleteClick = (po) => {
    setOpenMenuIndex(null);
    Swal.fire({
      html: `
        <div class="custom-icon-wrapper-swal">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Delete Purchase Order?</h2>
        <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
          Are you sure you want to delete Purchase Order <strong>${po.po_number}</strong>? This action cannot be undone.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete Purchase Order',
      cancelButtonText: 'No, Keep it',
      customClass: {
        popup: 'custom-swal-popup',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
        actions: 'custom-swal-actions-row'
      },
      buttonsStyling: false
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/purchase-orders/${po.id}`);
          if (response.data.success) {
            toast.success('Purchase Order deleted successfully');
            fetchPurchaseOrders();
          }
        } catch (error) {
          console.error('Error deleting Purchase Order:', error);
          toast.error(error.response?.data?.message || 'Failed to delete Purchase Order');
        }
      }
    });
  };

  const metrics = [
    {
      label: 'Total Purchase Orders',
      value: summaryData.total_purchase_orders.toLocaleString(),
      icon: <ShoppingCart size={20} />,
      theme: 'blue'
    },
    {
      label: 'Total PO Value',
      value: `₹${parseFloat(summaryData.total_po_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: <IndianRupee size={20} />,
      theme: 'green',
      highlight: true
    },
    {
      label: 'Pending Payments',
      value: `₹${parseFloat(summaryData.pending_payments || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: <Clock size={20} />,
      theme: 'orange'
    },
    {
      label: 'Completed POs',
      value: summaryData.completed_pos.toLocaleString(),
      icon: <CheckCircle2 size={20} />,
      theme: 'purple'
    }
  ];

  const getStatusClass = (status) => {
    if (!status) return styles['status-pending'];
    const s = status.toLowerCase();
    switch (s) {
      case 'completed': return styles['status-completed'];
      case 'partial': return styles['status-partial'];
      case 'review': return styles['status-review'];
      case 'pending': return styles['status-pending'];
      case 'cancelled':
      case 'rejected': return styles['status-cancelled'];
      default: return styles['status-pending'];
    }
  };

  const getInspectionClass = (status) => {
    if (!status) return styles['status-pending'];
    const s = status.toLowerCase();
    switch (s) {
      case 'approved': return styles['status-approved'];
      case 'pending':
      case 'in_progress': return styles['status-pending'];
      case 'rejected': return styles['status-cancelled'];
      default: return styles['status-pending'];
    }
  };

  return (
    <div className={styles['po-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>{pageTitle}</h1>
              <p>Monitor and manage all purchase orders issued to vendors.</p>
            </div>
            <button className={styles['add-btn']} onClick={() => navigate(`${pathPrefix}/create`)}>
              <Plus size={20} />
              <span>Add Purchase Order</span>
            </button>
          </div>

          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {metrics[0]?.value} · {metrics[2]?.value} pending
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {metrics.map((m, i) => (
                <div key={i} className={`${styles['metric-card']} ${styles[m.theme]} ${m.highlight ? styles['highlight-card'] : ''}`}>
                  <div className={styles['metric-icon-box']}>
                    {m.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{m.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{m.value}</span>
                    </div>
                  </div>
                  {m.highlight && <div className={styles['glow-effect']}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search PO # or Vendor Name..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(option.value); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.project_id || filters.vendor_id || filters.status) && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Purchase Orders</h3>
                  <p>Filter records by project, vendor, or status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(p => p.id === filters.project_id)?.project_name
                    } : null}
                    onChange={(opt) => handleFilterChange('project_id', opt ? opt.value : '')}
                    options={filterData.projects.map(p => ({ value: p.id, label: p.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Vendor</label>
                  <CustomSelect
                    value={filters.vendor_id ? (() => {
                      const v = filterData.vendors.find(vendor => vendor.id === filters.vendor_id);
                      return v ? {
                        value: v.id,
                        label: v.vendor_name + (v.vendor_code ? ` (${v.vendor_code})` : '')
                      } : null;
                    })() : null}
                    onChange={(opt) => handleFilterChange('vendor_id', opt ? opt.value : '')}
                    options={filterData.vendors.map(v => ({ value: v.id, label: v.vendor_name + (v.vendor_code ? ` (${v.vendor_code})` : '') }))}
                    placeholder="All Vendors"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status ? {
                      value: filters.status,
                      label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1).replace('_', ' ')
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'design_review', label: 'Design Review' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'manufacturing', label: 'Manufacturing' },
                      { value: 'inspection', label: 'Inspection' },
                      { value: 'payment_done', label: 'Payment Done' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={[styles['custom-table-container'], 'card'].join(' ')}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>PO NUMBER</th>
                    <th>VENDOR NAME</th>
                    <th>PROJECT NAME</th>
                    <th>PO DATE</th>
                    <th>EXPECTED DELIVERY</th>
                    <th>TOTAL AMOUNT</th>
                    <th>ADVANCE</th>
                    <th>OUTSTANDING</th>
                    <th>STATUS</th>
                    <th>INSPECTION</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <tr>
                      <td colSpan="11" style={{ textAlign: 'center', padding: '40px' }}>
                        <Loader2 size={32} className={styles['spinner']} style={{ color: '#f59e0b', margin: '0 auto' }} />
                        <p style={{ color: '#64748b', marginTop: '10px' }}>Loading purchase orders...</p>
                      </td>
                    </tr>
                  ) : purchaseOrders.length > 0 ? (
                    purchaseOrders.map((po, index) => (
                      <tr key={po.id}>
                        <td className={styles['id-cell']}>
                          <div className={styles['receipt-info']}>
                            <span
                              className={styles['receipt-text']}
                              onClick={() => navigate(`${pathPrefix}/${po.id}`)}
                            >
                              {po.po_number}
                            </span>
                          </div>
                        </td>
                        <td className={styles['vendor-name']}>{po.vendor?.vendor_name}</td>
                        <td className={styles['project-name']}>{po.project?.project_name}</td>
                        <td className={styles['date-cell']}>{new Date(po.po_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td className={styles['date-cell']}>
                          {new Date(po.expected_delivery_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className={styles['amount-cell']}>{parseFloat(po.total_amount).toLocaleString('en-IN')}</td>
                        <td>{parseFloat(po.advance_paid_amount || 0).toLocaleString('en-IN')}</td>
                        <td className={`${styles['balance-cell']} ${parseFloat(po.total_amount) - parseFloat(po.advance_paid_amount || 0) > 0 ? styles['has-balance'] : ''}`}>
                          {(parseFloat(po.total_amount) - parseFloat(po.advance_paid_amount || 0)).toLocaleString('en-IN')}
                        </td>
                        <td>
                          <span className={`${styles['status-badge']} ${getStatusClass(po.status)}`}>
                            {po.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles['status-badge']} ${styles['badge-dot']} ${getInspectionClass(po.inspection_status || 'pending')}`}>
                            <div className={styles['dot']}></div>
                            {po.inspection_status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === po.id ? styles['active'] : ''}`}
                              onClick={(e) => toggleActionMenu(po.id, e)}
                            >
                              <MoreVertical size={18} />
                            </button>
                            {openMenuIndex === po.id && (
                              <div
                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                style={{
                                  position: 'fixed',
                                  top: menuPosition.top,
                                  left: menuPosition.left
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button className={styles['dropdown-item']} onClick={() => navigate(`${pathPrefix}/${po.id}`)}>
                                  <Eye size={16} /> View
                                </button>
                                <button className={styles['dropdown-item']} onClick={() => navigate(`${pathPrefix}/edit/${po.id}`)}>
                                  <Pencil size={16} /> Edit
                                </button>
                                {/* <button className={styles['dropdown-item']} onClick={() => navigate('/projects/upload-design', { state: { po: po } })}>
                                  <FileUp size={16} /> Upload Design
                                </button> */}
                                <div className={styles['dropdown-divider']}></div>
                                <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDeleteClick(po)}>
                                  <Trash2 size={16} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <NoData message="No Purchase Orders found." isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['po-cards']}>
              {tableLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} className={styles['spinner']} style={{ color: '#f59e0b', margin: '0 auto' }} />
                  <p style={{ color: '#64748b', marginTop: '10px' }}>Loading purchase orders...</p>
                </div>
              ) : purchaseOrders.length > 0 ? (
                purchaseOrders.map((po) => (
                  <div key={po.id} className={styles['po-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']}>{po.po_number}</span>
                        <span className={styles['card-amount']}>₹{parseFloat(po.total_amount).toLocaleString('en-IN')}</span>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={`${styles['action-btn']} ${openMenuIndex === po.id ? styles['active'] : ''}`}
                          onClick={(e) => toggleActionMenu(po.id, e)}
                        >
                          <MoreVertical size={18} />
                        </button>
                        {openMenuIndex === po.id && (
                          <div
                            className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                            style={{
                              position: 'fixed',
                              top: menuPosition.top,
                              left: menuPosition.left
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button className={styles['dropdown-item']} onClick={() => navigate(`${pathPrefix}/${po.id}`)}>
                              <Eye size={16} /> View
                            </button>
                            <button className={styles['dropdown-item']} onClick={() => navigate(`${pathPrefix}/edit/${po.id}`)}>
                              <Pencil size={16} /> Edit
                            </button>
                            {/* <button className={styles['dropdown-item']} onClick={() => navigate('/projects/upload-design', { state: { po: po } })}>
                              <FileUp size={16} /> Upload Design
                            </button> */}
                            <div className={styles['dropdown-divider']}></div>
                            <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDeleteClick(po)}>
                              <Trash2 size={16} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Vendor</span>
                        <span className={styles['detail-value']}>{po.vendor?.vendor_name}</span>
                      </div>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Project</span>
                        <span className={styles['detail-value']}>{po.project?.project_name}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Status</span>
                        <span className={`${styles['status-badge']} ${getStatusClass(po.status)}`} style={{ minWidth: 'auto', fontSize: '10px', padding: '4px 8px' }}>
                          {po.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Outstanding</span>
                        <span className={`${styles['detail-value']} ${parseFloat(po.total_amount) - parseFloat(po.advance_paid_amount || 0) > 0 ? styles['has-balance'] : ''}`}>
                          ₹{(parseFloat(po.total_amount) - parseFloat(po.advance_paid_amount || 0)).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <div className={styles['footer-left']}>
                        <Calendar size={14} />
                        <span>{new Date(po.po_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message="No Purchase Orders found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={paginationData.current_page}
                totalItems={paginationData.total}
                itemsPerPage={paginationData.per_page}
                onPageChange={(page) => { setCurrentPage(page); navigate(`${pathPrefix}?page=${page}`, { replace: true }); }}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => { setCurrentPage(page); navigate(`${pathPrefix}?page=${page}`, { replace: true }); }}
            />
          </div>

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default PurchaseOrders;
